import { useEffect, useState } from 'react';

import { fetchSessionNotifications } from './api';
import { isApiRateLimited, nextSessionPollMs, SESSION_POLL_MS } from './sessionSync';
import { subscribeSharedPoll } from './sharedPoll';
import {
  SESSION_NOTIFICATION_POLL_MS,
  maybeShowBrowserNotifications,
  sanitizeNotification,
  uniqueJoinableSessionNotifications,
} from './sessionNotifications';

const useSessionNotifications = (enabled, intervalMs = SESSION_NOTIFICATION_POLL_MS) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setItems([]);
      return undefined;
    }

    let cancelled = false;
    const stop = subscribeSharedPoll({
      key: 'session-notifications',
      intervalMs,
      fetchFn: fetchSessionNotifications,
      skipWhenHidden: true,
      // Keep session/join alerts flowing in a background tab (slower).
      hiddenIntervalMs: 30000,
      shouldBackoff: isApiRateLimited,
      nextDelayMs: (error, previousMs) =>
        nextSessionPollMs(error, Math.max(previousMs, SESSION_POLL_MS)),
      onData: (error, payload) => {
        if (cancelled) {
          return;
        }
        if (error) {
          if (isApiRateLimited(error)) {
            setItems(prev => uniqueJoinableSessionNotifications(prev));
            return;
          }
          return;
        }
        const next = uniqueJoinableSessionNotifications(
          (payload?.data?.items || []).map(sanitizeNotification).filter(Boolean)
        );
        setItems(next);
        maybeShowBrowserNotifications(next);
      },
    });
    const expireTick = window.setInterval(() => {
      if (!cancelled) {
        setItems(prev => uniqueJoinableSessionNotifications(prev));
      }
    }, 15000);
    return () => {
      cancelled = true;
      stop();
      window.clearInterval(expireTick);
    };
  }, [enabled, intervalMs]);

  return items;
};

export default useSessionNotifications;
